<template>
    <base-panel
        :panel="panel"
        v-on:continue="handleContinue()"
        v-on:edit-action="handleEditAction($event)"
        v-on:update-width="handleUpdateWidth($event)"
        ref="basePanel"
    >
        <template slot="content">
            <session-button
                v-for="choice in panel.choices"
                :key="choice.id"
                @click="selectChoice(choice)"
                class="btn btn-block btn-branch btn-softgreen btn-lg btn-session"
                :class="{ 'btn-explored': choice.isExplored }"
                :loading="choice.showLoading"
                :disabled="buttonIsDisabled(choice)"
            >
                <span class="align-middle">{{ choice.text }}</span>
                <div class="d-inline-block align-middle unexplored-btn-marker-container position-relative">
                    <div
                        v-show="!choice.isExplored"
                        aria-label="Button is unexplored"
                        class="unexplored-btn-marker rounded-circle"
                    ></div>
                </div>
            </session-button>
        </template>
        <template slot="modal">
            <!-- Choice Modal -->
            <base-modal :id="choiceModalId" :centered="true" :size="choiceModalSize" class="global-theme">
                <template slot="title">
                    <span
                        v-show="currentChoice && currentChoice.correct === true"
                        aria-label="Correct selection"
                        class="icon modal-icon icon-glyphicons-basic-circle-empty-check feedback-icon align-middle text-success icon-mr rounded-circle correct-icon-animate animate-delay"
                    ></span>
                    <span
                        v-show="currentChoice && currentChoice.correct === false"
                        aria-label="Incorrect selection"
                        class="icon modal-icon icon-glyphicons-basic-circle-empty-remove feedback-icon align-middle text-danger icon-mr incorrect-icon-animate animate-delay"
                    ></span>
                    <span v-show="currentChoice && currentChoice.text" class="align-middle">{{
                        currentChoice.text
                    }}</span>
                </template>
                <template>
                    <div class="global-theme-text">
                        <rich-content-display
                            v-if="currentChoice && currentChoice.action === 'show_content_modal'"
                            :elements="currentChoice.elements"
                            :editable="editMode"
                        ></rich-content-display>
                    </div>
                </template>
                <template slot="footer">
                    <button class="btn btn-secondary btn-session" type="button" data-dismiss="modal">
                        {{ labelsTranslated.close }}
                    </button>
                </template>
            </base-modal>
        </template>
    </base-panel>
</template>

<script>
import http from "@/axios.js"
import BasePanel from "@/components/panels/BasePanel"
import BaseModal from "@/components/BaseModal"
import RichContentDisplay from "@/components/RichContentDisplay"
import SessionButton from "@/components/SessionButton"

export default {
    name: "ButtonsPanel",
    components: {
        BaseModal,
        RichContentDisplay,
        BasePanel,
        SessionButton,
    },
    props: {
        node: {
            type: Object,
            required: true,
        },
        panel: {
            type: Object,
            required: true,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        isPreviouslyVisited: {
            type: Boolean,
            required: false,
            default: false,
        },
        sessionId: {
            type: [String, Number],
            required: false,
        },
    },
    data() {
        return {
            branchingVideoComplete: this.$store.getters.preContentVideoComplete,
            currentChoice: {
                correct: null,
            },
        }
    },
    computed: {
        choices() {
            return this.panel.choices
        },
        correctChoicesCount() {
            return this.choices.filter((c) => c.correct && c.isExplored).length
        },
        choiceModalSize() {
            if (!this.currentChoice) {
                return ""
            } else if (this.currentChoice.modal_width == "large") {
                return "large"
            } else if (this.currentChoice.modal_width == "extra_large") {
                return "xl"
            }
            return ""
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        choiceModalId() {
            return `choice-modal-${this._uid}`
        },
        choiceModalSelector() {
            return `#${this.choiceModalId}`
        },
        editMode() {
            return this.$store.getters.previewEditMode
        },
    },
    watch: {
        correctChoicesCount(val) {
            this.$emit("update:correctChoicesCount", val)
        },
    },
    methods: {
        populateSavedResponses() {
            let vm = this
            this.savedResponses.forEach((savedResponse) => {
                let choice = vm.panel.choices.find((choice) => choice.id == savedResponse.choice.id)
                if (choice) {
                    vm.$set(choice, "isExplored", true)
                    vm.$set(choice, "correct", savedResponse.choice.correct)
                }
            })
        },
        // Handles a button selection.
        selectChoice(choice) {
            this.$set(choice, "isExplored", true)

            let contentModalWasShown = false
            if (choice.action === "show_content_modal") {
                // The choice action is already loaded, so we can show the content modal
                // before waiting for the response from the server.
                this.currentChoice = choice
                this.showContentModal()
                contentModalWasShown = true
            } else {
                // Reset the current choice to avoid showing the last selected choice
                // to the user before the new one is loaded.
                this.currentChoice = {
                    correct: null,
                }
                this.$set(choice, "showLoading", true)
            }

            let vm = this
            http.post(`/api/students/sessions/${this.sessionId}/branch/${choice.id}/`, {
                responses: this.currentResponses,
            })
                .then(function (response) {
                    let currentChoice = response.data.choice
                    // Only update the current choice if it's not already loaded.
                    if (!vm.currentChoice.id) {
                        vm.currentChoice = response.data.choice
                    }

                    // Save correctness and message.
                    vm.$set(choice, "correct", currentChoice.correct)
                    vm.$set(choice, "action", currentChoice.action)
                    // Only update the elements attached to the choice if they're not already loaded.
                    if (!choice.elements) {
                        vm.$set(choice, "elements", currentChoice.elements)
                    }

                    // Handle choice action.
                    if (currentChoice.action === "show_content_modal" && !contentModalWasShown) {
                        vm.showContentModal()
                    } else if (currentChoice.action === "proceed_to_node") {
                        // Update the session, which includes the new node.
                        vm.$emit("update-session", response.data.session)
                    }

                    // Activate or deactivate vitals triggers included with the payload.
                    let vitalsTriggers = currentChoice.vitals_triggers
                    if (vitalsTriggers.length > 0) {
                        vm.$emit("trigger-vitals", { triggers: vitalsTriggers, activate: true })
                    }

                    vm.$emit("response-received")
                })
                .finally(function () {
                    choice.showLoading = false
                })
        },
        showContentModal() {
            $(this.choiceModalSelector).modal("show")
        },
        buttonIsDisabled(choice) {
            let firstSelectionDisabled = this.panel.disable_items_after_selection && choice.isExplored
            return choice.showLoading || firstSelectionDisabled
        },
        handleContinue() {
            this.$emit("continue")
        },
        handleEditAction(action) {
            this.$emit("edit-action", action)
        },
        handleUpdateWidth($event) {
            this.$emit("update-width", $event)
        },
        skipVideos() {
            this.$refs.basePanel.skipVideos()
        },
    },
    mounted() {
        // Make sure when the modal is closed, the content is removed. This stops things like
        // audio files and videos from playing the background after the learner has clicked away.
        let vm = this
        $(this.choiceModalSelector).on("hidden.bs.modal", function (event) {
            vm.currentChoice = {
                correct: null,
            }
        })
        this.populateSavedResponses()
    },
    beforeDestroy() {
        // Remove event listeners on the choice modal.
        $(this.choiceModalSelector).off()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.btn-branch {
    font-size: 17px;
    @media (min-width: $breakpoint-sm) {
        max-width: 100%;
        padding-left: 20px;
        padding-right: 20px;
    }
}

.unexplored-btn-marker-container {
    width: 0px;
    left: 8px;
    padding-top: 1px;
}

.unexplored-btn-marker {
    width: 6px;
    height: 6px;
    background-color: #55f3e3;
    animation: 1.5s ease-in-out 0s infinite normal both running unex-pulse;
}

.button-spinner {
    margin-left: 6px;
    vertical-align: -4px;
}

.loading-spacer {
    width: $spinner-width-sm;
    display: inline-block;
}

@keyframes unex-pulse {
    0% {
        transform: scale(1.05);
    }
    50% {
        transform: scale(1.23);
    }
    100% {
        transform: scale(1.05);
    }
}

// .btn-session {
//     font-weight: 400;
// }
</style>